import { SEASONAL } from "../data/seasonal";
import { T } from "../styles/theme";
import type { LastDoneMap } from "../types";

interface SeasonalViewProps {
  lastDone: LastDoneMap;
  setLastDone: (next: (prev: LastDoneMap) => LastDoneMap) => void;
  editingDate: string | null;
  setEditingDate: (id: string | null) => void;
}

/** The "Seasonal Maintenance" tab — quarterly + biannual tasks with last-done dates. */
export function SeasonalView({ lastDone, setLastDone, editingDate, setEditingDate }: SeasonalViewProps) {
  return (
    <div>
      <div
        style={{
          background: "linear-gradient(135deg,#f0f0ff,#fff)",
          borderRadius: 20,
          padding: "18px 18px 14px",
          marginBottom: 14,
          border: "1.5px solid #9b6dd444",
          boxShadow: "0 4px 20px #9b6dd422",
        }}
      >
        <div style={{ fontSize: 26, marginBottom: 4 }}>🔄</div>
        <h2 style={{ margin: 0, fontSize: 20, color: "#3d1e5c", fontFamily: T.display, fontWeight: 700 }}>
          Seasonal Maintenance
        </h2>
        <p style={{ margin: "5px 0 0", fontSize: 12, color: T.inkMid, fontStyle: "italic", lineHeight: 1.5 }}>
          {SEASONAL.description}
        </p>
        <div
          style={{
            marginTop: 10,
            background: "#f5f0ff",
            borderRadius: 10,
            padding: "9px 12px",
            fontSize: 11,
            color: "#6b3fa0",
            lineHeight: 1.5,
            border: "1px solid #9b6dd433",
          }}
        >
          💡 Tap <strong>Mark done</strong> on any task to log today's date. That's your tracker — no guessing when you last did it.
        </div>
      </div>

      {SEASONAL.groups.map((group) => (
        <div
          key={group.id}
          style={{
            background: T.white,
            borderRadius: 16,
            marginBottom: 12,
            overflow: "hidden",
            boxShadow: "0 2px 12px rgba(45,32,22,0.07)",
            border: `1px solid ${group.accent}33`,
          }}
        >
          <div
            style={{
              padding: "10px 16px",
              background: group.color,
              borderBottom: `1px solid ${group.accent}22`,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 700, color: group.accent, fontFamily: T.display }}>
              {group.label}
            </span>
          </div>
          {group.tasks.map((task, i) => {
            const done = lastDone[task.id];
            const isEditing = editingDate === task.id;
            return (
              <div
                key={task.id}
                style={{
                  padding: "13px 16px",
                  borderBottom: i < group.tasks.length - 1 ? `1px solid ${T.cream}` : "none",
                  background: done ? "#f8fdf8" : T.white,
                  borderLeft: `3px solid ${done ? T.sage : group.accent}`,
                  transition: "background 0.2s",
                }}
              >
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, color: T.ink, lineHeight: 1.45 }}>{task.label}</div>
                    {done ? (
                      <div
                        style={{
                          marginTop: 4,
                          fontSize: 10,
                          color: T.sageDark,
                          background: T.sagePale,
                          borderRadius: 6,
                          padding: "2px 8px",
                          display: "inline-block",
                        }}
                      >
                        ✓ Last done: {done}
                      </div>
                    ) : (
                      <div style={{ marginTop: 4, fontSize: 10, color: T.inkLight, fontStyle: "italic" }}>
                        Not logged yet
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => setEditingDate(isEditing ? null : task.id)}
                    style={{
                      flexShrink: 0,
                      padding: "5px 10px",
                      fontSize: 10,
                      fontWeight: 700,
                      background: done ? "#eafaf0" : group.color,
                      color: done ? T.sageDark : group.accent,
                      border: `1.5px solid ${done ? T.sage : group.accent}44`,
                      borderRadius: 20,
                      cursor: "pointer",
                      fontFamily: T.body,
                      transition: "all 0.2s",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {done ? "Update" : "Mark done"}
                  </button>
                </div>
                {isEditing && (
                  <div style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <button
                      onClick={() => {
                        const d = new Date();
                        const label = d.toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        });
                        setLastDone((p) => ({ ...p, [task.id]: label }));
                        setEditingDate(null);
                      }}
                      style={{
                        padding: "6px 14px",
                        fontSize: 11,
                        fontWeight: 700,
                        background: group.accent,
                        color: "#fff",
                        border: "none",
                        borderRadius: 20,
                        cursor: "pointer",
                        fontFamily: T.body,
                      }}
                    >
                      ✓ Mark as done today
                    </button>
                    <button
                      onClick={() => {
                        setLastDone((p) => {
                          const n = { ...p };
                          delete n[task.id];
                          return n;
                        });
                        setEditingDate(null);
                      }}
                      style={{
                        padding: "6px 12px",
                        fontSize: 11,
                        color: T.inkLight,
                        background: T.parchment,
                        border: "none",
                        borderRadius: 20,
                        cursor: "pointer",
                        fontFamily: T.body,
                      }}
                    >
                      Clear date
                    </button>
                    <button
                      onClick={() => setEditingDate(null)}
                      style={{
                        padding: "6px 10px",
                        fontSize: 11,
                        color: T.inkLight,
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
